import { Trans, useTranslation } from 'react-i18next';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { useDeleteClip } from '../../hooks/use-clips';
import { notifyError, notifySuccess } from '../../lib/toast';

interface DeleteClipDialogProps {
  open: boolean;
  onClose: () => void;
  clipId: string;
  clipTitle: string;
  // Called after a successful delete — e.g. the editor navigates back to the list.
  onDeleted?: () => void;
}

// Confirm dialog for hard-deleting a clip. Kept distinct from the (lighter-weight) unpublish
// dialog because the copy + the call-to-action style differ enough that one dialog handling
// both modes would either bury the destructive nature or over-warn for the safe op.
//
// Disabled-state contract: the caller MUST gate the trigger button — this dialog assumes the
// clip is unpublished and in a deletable state. The API still 409s on a published clip as a
// safety net (race: someone published from another tab), but we surface that via the toast.
export function DeleteClipDialog({ open, onClose, clipId, clipTitle, onDeleted }: DeleteClipDialogProps) {
  const { t } = useTranslation();
  const del = useDeleteClip(clipId);

  async function onConfirm() {
    try {
      await del.mutateAsync();
      notifySuccess(t('admin.clips.deleteDialog.confirmToast', { title: clipTitle }));
      onClose();
      onDeleted?.();
    } catch (err) {
      notifyError(err, t('admin.clips.deleteDialog.errorToast'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={del.isPending ? () => {} : onClose}
      title={t('admin.clips.deleteDialog.title')}
      sub={clipTitle}
      panelClass="max-w-md"
      footer={
        <>
          <Button onClick={onClose} disabled={del.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-3.5 h-3.5" /> : <Icon.trash className="w-3.5 h-3.5" />}
            {t('admin.clips.deleteDialog.deleteButton')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-ink-2">
        <p>
          {t('admin.clips.deleteDialog.bodyMain')}
          <span className="font-semibold text-ink"> {t('admin.clips.deleteDialog.bodyEmphasis')}</span>
        </p>
        <ul className="ml-4 list-disc text-[12.5px] text-muted">
          <li>
            <Trans
              i18nKey="admin.clips.deleteDialog.sourceBucket"
              values={{ clipId }}
              components={{ code: <span className="font-mono" /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="admin.clips.deleteDialog.processedBucket"
              values={{ clipId }}
              components={{ code: <span className="font-mono" /> }}
            />
          </li>
          <li>{t('admin.clips.deleteDialog.auditNote')}</li>
        </ul>
      </div>
    </Modal>
  );
}
